import { defineStore } from 'pinia'
import { ref } from 'vue'
import { axiosClient } from '@/utils/axios-client'
import type { UUID } from 'node:crypto'

import type { JourneyTemplateMinimal } from '@/model/edit-journey.model.ts'
import {
  createSignature,
  type BgbFields,
  type EcmrFields,
  type ShipmentFields,
  type Template,
} from '@logistiek-zonder-papier/lzp-library'
import { useUserStore } from '@/store/user.store'
import { emptyToNull } from '@/utils/post'

export const useTemplateStore = defineStore('templateStore', () => {
  /**
   * Fetch: {@link fetchAllTemplatesMinimal}
   */
  const minimalTemplates = ref<JourneyTemplateMinimal[]>([])
  const userStore = useUserStore()
  /**
   * Fetch: {@link fetchAll}
   */
  const templates = ref<Template[]>([])

  async function createTemplate(template: Template) {
    const payload = await createPayload(template)

    await axiosClient.request({
      url: '/shipment-templates',
      method: 'POST',
      data: payload,
    })
  }

  async function createPayload(template: Template) {
    const currentUser = await userStore.fetchMe()

    const payload: Partial<
      ShipmentFields & { bgb?: Partial<BgbFields>; ecmr: Partial<EcmrFields> }
    > = { ...template }

    payload.customerId = currentUser.customerId

    if (template.type === 'bgb' && template.bgb) {
      payload.bgb = { ...template.bgb }

      if (!template.bgb.field2?.name) {
        delete payload.bgb.field2
      }

      if (!template.bgb.field4A?.name) {
        delete payload.bgb.field4A
      }

      if (payload.toPerson != null) {
        payload.toPerson.address = { ...template.toAddress }
      }
    } else if (template.type === 'ecmr' && template.ecmr) {
      payload.ecmr = { ...template.ecmr }
      if (template.ecmr.carrier2.name == null) {
        delete payload.ecmr.carrier2
      }

      payload.ecmr.signatureSender = createSignature(
        currentUser.id,
        `${currentUser.firstName} ${currentUser.lastName}`,
        currentUser.email
      )
    }

    return emptyToNull(payload)
  }

  async function updateTemplate(template: Template) {
    const payload = await createPayload(template)
    await axiosClient.request({
      url: `/shipment-templates/${template.id}`,
      method: 'PATCH',
      data: payload,
    })
  }

  /**
   * Data: {@link templates}
   */
  async function fetchAll() {
    if (templates.value.length > 0) {
      return
    }

    const response = await axiosClient.request<Template[]>({
      url: '/shipment-templates/total',
      method: 'GET',
    })

    templates.value = response.data
  }

  async function fetch(id: UUID) {
    const response = await axiosClient.request<Template>({
      url: `/shipment-templates/total/${id}`,
      method: 'GET',
    })

    return response.data
  }

  async function fetchByProject(id: UUID) {
    const response = await axiosClient.request<Template[]>({
      url: `/shipment-templates/total`,
      method: 'GET',
      params: { projectId: id },
    })

    return response.data
  }

  /**
   * Data: {@link minimalTemplates}
   */
  async function fetchAllTemplatesMinimal() {
    if (minimalTemplates.value.length > 0) {
      return
    }

    const response = await axiosClient.request<JourneyTemplateMinimal[]>({
      url: '/idf/api/stroom/templates',
    })

    minimalTemplates.value = response.data
  }

  return {
    templates,
    minimalTemplates,
    fetchAllTemplatesMinimal,
    createTemplate,
    updateTemplate,
    fetchAll,
    fetch,
    fetchByProject,
  }
})
